// src/components/PriceCalcComponent.js
export default (config = {}) => ({
  // Configuration inputs
  pricelist_base: config.pricelist_base || [],
  pricelist_extra: config.pricelist_extra || [],
  pax: config.pax || 2,
  mode: config.mode || 'private',
  target: config.target || 'public', // tiers are filtered by target first
  excludeIva: config.excludeIva === undefined ? false : config.excludeIva,
  ivaPercentage: config.ivaPercentage || 21,

  // --- Cascading Filtering for Base Tiers ---
  init() {
    // console.log({excludeIva: config.excludeIva, ivaPercentage: this.ivaPercentage});
    
    const container = this.$el;
    // add the withIVA and withoutIVA inside the tiers of each pricelist.
    this.pricelist_base.forEach(item => {
      item.withIVA = this.convertPrice(
        item.base_price,
        item.iva_included,
        true,
        1 + this.ivaPercentage / 100
      );
      item.withoutIVA = this.convertPrice(
        item.base_price,
        item.iva_included,
        false,
        1 + this.ivaPercentage / 100
      );})
    this.pricelist_extra.forEach(item => {
      item.withIVA = this.convertPrice(
        item.price_per_pax,
        item.iva_included,
        true,
        1 + this.ivaPercentage / 100
      );
      item.withoutIVA = this.convertPrice(
        item.price_per_pax,
        item.iva_included,
        false,
        1 + this.ivaPercentage / 100
      );})
        
    this.$nextTick(() => {
      // set the new correct maxHeight on container
      // if (container.style.maxHeight !== "0px") container.style.maxHeight = container.scrollHeight + 'px';
    });
  },
  get availableModes() {
    const modes = new Set();
    this.pricelist_base.forEach(item => {
      if (item.price_type) {
        modes.add(item.price_type.toLowerCase());
      }
    });
    this.pricelist_extra.forEach(item => {
      if (item.price_type) {
        modes.add(item.price_type.toLowerCase());
      }
    });
    return Array.from(modes).sort();
  },
  // 1. Filter by target.
  get baseTiersByTarget() {
    const targetLower = this.target.toLowerCase();
    return this.pricelist_base.filter(item =>
      ((item.price_target ? item.price_target.toLowerCase() : 'public') === targetLower) &&
      item.hasOwnProperty('base_price')
    ).sort((a, b) => {
      const modeComparison = a.price_type.localeCompare(b.price_type);
      if (modeComparison !== 0) return modeComparison;
      return (a.pax_total_min || 0) - (b.pax_total_min || 0);
    });
  },
  // 2. Filter the above by mode.
  get baseTiersByMode() {
    const modeLower = this.mode.toLowerCase();
    return this.baseTiersByTarget.filter(item =>
      item.price_type.toLowerCase() === modeLower
    );
  },
  // 3. Sort by (pax_total_min || 0) ascending and select the last tier whose pax_total_min is <= current pax.
  get applicableBaseTier() {
    const sorted = [...this.baseTiersByMode]
    let candidate = null;
    for (let item of sorted) {
      if ((item.pax_total_min || 0) <= this.pax) {
        candidate = item;
      }
    }
    return candidate;
  },

  // --- Cascading Filtering for Extra Tiers ---
  // --- New Getter: Targeted Tiers --- 
  // Produces an array of objects with the keys:
  //   pax_total_min, price_type, base, extra
  // It groups tiers (from baseTiersByTarget and extraTiersByTarget) by the unique combination of pax_total_min and price_type.
  get targetedTiersByPaxTotalMin() {
    const mapping = {};
    // Process base tiers filtered by target.
    this.baseTiersByTarget.forEach(tier => {
      const key = `${tier.pax_total_min || 0}-${tier.price_type.toLowerCase()}`;
      if (!mapping[key]) {
        mapping[key] = {
          pax_total_min: tier.pax_total_min || 0,
          price_type: tier.price_type.toLowerCase()
        };
      }
      mapping[key].base = tier;
    });
    // Process extra tiers filtered by target.
    this.extraTiersByTarget.forEach(tier => {
      const key = `${tier.pax_total_min || 0}-${tier.price_type.toLowerCase()}`;
      if (!mapping[key]) {
        mapping[key] = {
          pax_total_min: tier.pax_total_min || 0,
          price_type: tier.price_type.toLowerCase()
        };
      }
      mapping[key].extra = tier;
    });
    return Object.values(mapping).sort((a, b) => {
      const modeComparison = a.price_type.localeCompare(b.price_type);
      if (modeComparison !== 0) return modeComparison;
      return (a.pax_total_min || 0) - (b.pax_total_min || 0);
    });
  },
  // 1. Filter by target.
  get extraTiersByTarget() {
    const targetLower = this.target.toLowerCase();
    return this.pricelist_extra.filter(item =>
      ((item.price_target ? item.price_target.toLowerCase() : 'public') === targetLower) &&
      item.hasOwnProperty('price_per_pax')
    ).sort((a, b) => {
      const modeComparison = a.price_type.localeCompare(b.price_type);
      if (modeComparison !== 0) return modeComparison;
      return (a.pax_total_min || 0) - (b.pax_total_min || 0);
    });
  },
  // 2. Filter the above by mode.
  get extraTiersByMode() {
    const modeLower = this.mode.toLowerCase();
    return this.extraTiersByTarget.filter(item =>
      item.price_type.toLowerCase() === modeLower
    );
  },
  // 3. Sort by (pax_total_min || 0) ascending and select the last tier where (tier.pax_total_min || 0) - base_included <= extra pax.
  get applicableExtraTier() {
    const base = this.applicableBaseTier;
    const base_included = base ? base.pax_included : 0;
    const extraPax = this.pax - base_included;
    const sorted = [...this.extraTiersByMode]
    let candidate = null;
    for (let item of sorted) {
      if (((item.pax_total_min || 0) - base_included) <= extraPax) {
        candidate = item;
      }
    }
    return candidate;
  },

  // --- Price Conversion Helper ---
  // Converts a price to a number and applies IVA conversion if necessary.
  convertPrice(value, currentIvaIncluded, targetIvaIncluded, factor) {
    const numericValue = Number(value);
    if (currentIvaIncluded === targetIvaIncluded) return numericValue;
    return currentIvaIncluded ? numericValue / factor : numericValue * factor;
  },

  // --- Breakdown Calculation ---
  // Calculates the pricing breakdown based on the applicable tiers.
  get breakdown() {
    const factor = 1 + this.ivaPercentage / 100;
    const applicableBase = this.applicableBaseTier;
    const base_included = applicableBase ? applicableBase.pax_included : 0;
    const extraPax = this.pax - base_included;

    let baseWithIVA = 0, baseWithoutIVA = 0;
    if (applicableBase) {
      baseWithIVA = this.convertPrice(
        applicableBase.base_price,
        applicableBase.iva_included,
        true,
        factor
      );
      baseWithoutIVA = this.convertPrice(
        applicableBase.base_price,
        applicableBase.iva_included,
        false,
        factor
      );
    }

    const applicableExtra = this.applicableExtraTier;
    let extraWithIVA = 0, extraWithoutIVA = 0;
    if (applicableExtra) {
      extraWithIVA = this.convertPrice(
        applicableExtra.price_per_pax,
        applicableExtra.iva_included,
        true,
        factor
      );
      extraWithoutIVA = this.convertPrice(
        applicableExtra.price_per_pax,
        applicableExtra.iva_included,
        false,
        factor
      );
    }

    let finalWithIVA = 0, finalWithoutIVA = 0;
    if (applicableExtra) {
      finalWithIVA = baseWithIVA + (extraPax > 0 ? extraPax * extraWithIVA : 0);
      finalWithoutIVA = baseWithoutIVA + (extraPax > 0 ? extraPax * extraWithoutIVA : 0);
    } else if (extraPax === 0) {
      finalWithIVA = baseWithIVA;
      finalWithoutIVA = baseWithoutIVA;
    }

    return {
      base: {
        tier: applicableBase,
        withIVA: baseWithIVA,
        withoutIVA: baseWithoutIVA,
      },
      extra: {
        tier: applicableExtra,
        quantity: extraPax,
        withIVA: extraWithIVA,
        withoutIVA: extraWithoutIVA,
        totalWithIVA: extraPax > 0 ? extraPax * extraWithIVA : 0,
        totalWithoutIVA: extraPax > 0 ? extraPax * extraWithoutIVA : 0,
      },
      final: {
        withIVA: finalWithIVA,
        withoutIVA: finalWithoutIVA,
      },
    };
  },

  // --- Helper Methods for Template Highlighting ---
  // Use these in your template to mark the active tier.
  isBaseTierApplicable(tier) {
    return tier === this.applicableBaseTier;
  },
  isExtraTierApplicable(tier) {
    return tier === this.applicableExtraTier;
  },

  // --- Display Outputs ---
  get calculatedPriceWithIVA() {
    return +this.breakdown.final.withIVA;
  },
  get calculatedPriceWithoutIVA() {
    return +this.breakdown.final.withoutIVA;
  },
  formatNumber(num, $locale ='es-ES', currency) {
    num = num + 0;
    if (isNaN(num)) return num;
    return new Intl.NumberFormat($locale, {
      style: currency ? 'currency' : 'decimal',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  }
});
